import {
  faChevronDown,
  faClone,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SubButton } from '@riddler-co-jp/specc-ui-components';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useChangeEventStatusMutation } from '../../../api/__generated__/event.generated';
import {
  EventDocument,
  useEventData,
  useEventMembersData,
} from '../../../api/event';
import { useRetryableMutationWithUI } from '../../../lib/useRetryableMutationWithUI';
import { useCustomClaim } from '../../../redux/selectors/authSelectors';
import { useAdminEventId } from '../../../redux/selectors/gameSelectors';
import Colors from '../../../styles/colors';
import { EventStatus } from '../../../types';
import { AdminRole } from '../AdminAdministratorDetail';
import AdminCommonBG from '../AdminCommonBG';
import { HeaderButton } from '../UIelements/AdminNavigation';
import AdminEventController from './AdminEventController';
import { useEventCsvCall } from './hooks/useEventCsv';

interface AdminEventControlViewProps {}

export const EventResultCsvContext = React.createContext(null);

const AdminEventControlView: React.FC<AdminEventControlViewProps> = () => {
  const navigate = useNavigate();
  const [event, setEvent] = React.useState<EventDocument | null>(null);
  const [participantUids, setParticipantUids] = React.useState<string[]>([]);
  const eventId = useAdminEventId();
  console.log('eventId', eventId);
  const [initEvent] = useEventData(eventId);
  const [initEventMembers] = useEventMembersData(eventId);
  const customClaim = useCustomClaim();
  const role: AdminRole = customClaim?.commonRoles.includes('riddlerAdmin')
    ? 'riddlerAdmin'
    : customClaim?.commonRoles.includes('dentsuAdmin')
    ? 'dentsuAdmin'
    : 'organizationAdmin';

  React.useEffect(() => {
    if (initEvent !== undefined) {
      setEvent(initEvent);
    }
  }, [initEvent]);

  React.useEffect(() => {
    if (initEventMembers !== undefined) {
      setParticipantUids(initEventMembers.participants ?? []);
    }
  }, [initEventMembers]);

  const [startEvent] = useRetryableMutationWithUI(
    useChangeEventStatusMutation,
    {
      hookOptions: {
        variables: {
          input: {
            eventId: event?.eventId ?? '',
            status: EventStatus.Ongoing,
          },
        },
      },
    }
  );
  const onStartEvent = React.useCallback(() => {
    startEvent();
  }, [startEvent]);

  const [endEvent] = useRetryableMutationWithUI(useChangeEventStatusMutation, {
    hookOptions: {
      variables: {
        input: {
          eventId: event?.eventId ?? '',
          status: EventStatus.Ending,
        },
      },
    },
  });
  const onEndEvent = React.useCallback(() => {
    endEvent();
  }, [endEvent]);

  const onCompleted = React.useCallback(() => {
    navigate(`/admin/events`);
  }, [navigate]);

  const [removeEvent] = useRetryableMutationWithUI(
    useChangeEventStatusMutation,
    {
      hookOptions: {
        variables: {
          input: {
            eventId: event?.eventId ?? '',
            status: EventStatus.Removed,
          },
        },
        onCompleted,
      },
    }
  );
  const onRemoveEvent = React.useCallback(() => {
    removeEvent();
  }, [removeEvent]);

  const onEdit = React.useCallback(() => {
    navigate(`/admin/events/${eventId}/edit`);
  }, [eventId, navigate]);

  const { onCsvDownload, csvClicked, onCsvDownloadButton } = useEventCsvCall();

  let disableEditButton = false;
  if (event) {
    disableEditButton = event.status !== EventStatus.Preparing;
  }

  const btnCopy: HeaderButton = {
    buttonText: 'コピーを作成',
    disabled: false,
    buttonDom: (
      <CopyButton
        key={`subbutton_copy`}
        onClick={() => {
          navigate(`/admin/events/${eventId}/copy`);
        }}
      >
        <FontAwesomeIcon icon={faClone} />
        コピーを作成
      </CopyButton>
    ),
  };

  const btnEdit: HeaderButton = {
    buttonText: '編集する',
    event: onEdit,
    disabled: disableEditButton,
  };

  return (
    <AdminCommonBG
      title='イベント詳細'
      backButton
      headerButtons={[btnCopy, btnEdit]}
      downloadCsvButton={
        role === 'organizationAdmin' ? undefined : onCsvDownloadButton
      }
    >
      <AdminEventControlViewWrapper>
        {event && (
          <AdminEventController
            event={event}
            participantUids={participantUids}
            onStartEvent={onStartEvent}
            onEndEvent={onEndEvent}
            onRemoveEvent={onRemoveEvent}
            role={role}
            onCsvDownload={onCsvDownload}
            csvClicked={csvClicked}
          />
        )}
      </AdminEventControlViewWrapper>
    </AdminCommonBG>
  );
};

const AdminEventControlViewWrapper = styled.div``;

export default AdminEventControlView;

const CopyButton = styled.div`
  font-size: 1.3rem;
  color: ${Colors.accent};
  cursor: pointer;
  margin-left: 1rem;

  svg {
    margin-right: 0.6rem;
  }

  &:hover {
    opacity: 0.8;
  }
`;
